import React, { useEffect, useState, useMemo } from 'react';
import Fichearticle from './Fichearticle';
import './Suiviprevision.css';

/* ===================================================
   ★ CONFIGURATION
   =================================================== */
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ Étapes affichées pendant le recalcul (script SSE)
const ETAPES = [
  'Chargement des commandes fournisseurs',
  'Chargement des commandes clients',
  'Import des prévisions',
  'Calcul du stock projeté',
  'Analyse des ruptures',
  'Calcul des dates de commande',
  'Enregistrement dans PostgreSQL',
  'Finalisation',
];

// ★ Seuils de couverture (commande ferme / prévision)
const SEUIL_OK = 80;      // ≥ 80 % = couvert
const SEUIL_FAIBLE = 10;  // < 10 % = faible

// ★ Mois passés non modifiables
const MOIS_BLOQUES = ['M-4', 'M-3', 'M-2', 'M-1'];

const OFFSET_PAR_LABEL = {
  'M-4': -4, 'M-3': -3, 'M-2': -2, 'M-1': -1,
  'M': 0, 'M+1': 1, 'M+2': 2, 'M+3': 3,
  'M+4': 4, 'M+5': 5, 'M+6': 6, 'M+7': 7,
  'M+8': 8, 'M+9': 9, 'M+10': 10, 'M+11': 11,
};
const ORDRE_MOIS = Object.keys(OFFSET_PAR_LABEL);
const MOIS_DEFAUT = ['M', 'M+1', 'M+2', 'M+3', 'M+4', 'M+5', 'M+6', 'M+7', 'M+8', 'M+9', 'M+10', 'M+11'];

/* ===================================================
   UTILITAIRES
   =================================================== */
const correspondMois = (dateStr, offsetMois) => {
  if (!dateStr) return false;
  const now = new Date();
  const cible = new Date(now.getFullYear(), now.getMonth() + offsetMois, 1);
  let d;
  if (typeof dateStr === 'string' && dateStr.includes('/')) {
    const [jour, moisStr, annee] = dateStr.split('/');
    d = new Date(Number(annee), Number(moisStr) - 1, Number(jour));
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d.getTime())) return false;
  return d.getFullYear() === cible.getFullYear() && d.getMonth() === cible.getMonth();
};

const getMoisLabel = (m) => {
  const offset = OFFSET_PAR_LABEL[m];
  if (offset === undefined) return '';
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return base.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

const fr = (n) => Number(n || 0).toLocaleString('fr-FR');

// Niveau de couverture d'une cellule
const niveau = (prev, ferme) => {
  if (!prev) return ferme > 0 ? 'over' : 'none';
  const pct = (ferme / prev) * 100;
  if (pct > 100) return 'over';
  if (pct >= SEUIL_OK) return 'ok';
  if (pct >= SEUIL_FAIBLE) return 'mid';
  return 'low';
};

const RANG = { over: 4, low: 3, mid: 2, none: 1, ok: 0 };

const STATUT = {
  over: 'Sur-commande',
  low: 'Faible couverture',
  mid: 'En cours',
  ok: 'Couvert',
  none: 'Sans prévision',
};

const FILTRES = [
  { key: 'all', label: 'Toutes' },
  { key: 'over', label: 'Sur-commande' },
  { key: 'low', label: 'Faible couverture' },
  { key: 'ok', label: 'Couvertes' },
];

/* ===================================================
   COMPOSANT
   =================================================== */
const Suiviprevision = () => {
  const [previsions, setPrevisions] = useState([]);
  const [references, setReferences] = useState([]);       // [{ item, desc }]
  const [commandesClient, setCommandesClient] = useState([]);
  const [modalItem, setModalItem] = useState(null);
  const [searchRef, setSearchRef] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [filtre, setFiltre] = useState('all');

  useEffect(() => {
    document.title = 'MYSSA – Suivi prévisions';
  }, []);

  /* ---------- Chargement ---------- */
  useEffect(() => {
    fetch(`${API}/prevision`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => setPrevisions(data.map(e => ({
        ITEM: e.Item, mois: e.Mois,
        Prevision: e.Prevision, 'Commande ferme': e.Commande_ferme,
      }))))
      .catch(err => console.error('Erreur prévisions :', err));

    fetch(`${API}/base_de_donne_item`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const map = new Map();
        data.forEach(i => {
          if (i.Item && !map.has(i.Item)) map.set(i.Item, i.Description || i.description || '');
        });
        setReferences(Array.from(map, ([item, desc]) => ({ item, desc })));
      })
      .catch(err => console.error('Erreur références :', err));

    fetch(`${API}/base_de_donnee_cmde_client`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => setCommandesClient(data))
      .catch(err => console.error('Erreur commandes :', err));
  }, []);

  const descMap = useMemo(() => {
    const m = {};
    references.forEach(r => { m[r.item] = r.desc; });
    return m;
  }, [references]);

  /* ---------- Script SSE ---------- */
  const lancerScript = () => {
    setIsRunning(true); setProgress(0); setStepIndex(0);
    setIsDone(false); setScriptError(false); setErrorMsg('');
    const eventSource = new EventSource(`${API}/run-script`);
    let etapeIndex = 0;
    eventSource.onmessage = (event) => {
      const message = event.data;
      if (message === '__DONE__') {
        setProgress(100); setStepIndex(ETAPES.length); setIsDone(true);
        eventSource.close(); return;
      }
      if (message.startsWith('ERREUR:')) {
        setScriptError(true); setErrorMsg('Erreur détectée pendant le calcul');
        eventSource.close(); return;
      }
      etapeIndex = Math.min(etapeIndex + 1, ETAPES.length - 1);
      setProgress(Math.round((etapeIndex / ETAPES.length) * 95));
      setStepIndex(etapeIndex);
    };
    eventSource.onerror = () => {
      setScriptError(true); setErrorMsg('Connexion interrompue');
      eventSource.close();
    };
  };

  const fermerModal = () => {
    setIsRunning(false); setProgress(0); setStepIndex(0);
    setIsDone(false); setScriptError(false); setErrorMsg('');
  };

  /* ---------- Ajout d'une référence ---------- */
  const handleItemChange = (value) => {
    if (!value) return;
    const dejaPresent = previsions.some(p => p.ITEM === value);
    if (dejaPresent) { alert(`La référence "${value}" est déjà présente.`); return; }
    const moisExistants = [...new Set(previsions.map(i => i.mois))];
    const moisAUtiliser = moisExistants.length > 0 ? moisExistants : MOIS_DEFAUT;
    const commandesItem = commandesClient.filter(c => c.Item === value);
    const newEntries = moisAUtiliser.map(m => {
      const offset = OFFSET_PAR_LABEL[m];
      const commandeFerme = offset !== undefined
        ? commandesItem.filter(c => correspondMois(c['Date de livraison prevue'], offset))
          .reduce((acc, c) => acc + (Number(c.RAL) || 0), 0)
        : 0;
      return { ITEM: value, mois: m, Prevision: 0, 'Commande ferme': commandeFerme };
    });
    setPrevisions(prev => [...prev, ...newEntries]);
    setFiltre('all');
  };

  /* ---------- Sauvegarde ---------- */
  const handleSave = async () => {
    if (previsions.length === 0) {
      if (!window.confirm('Tableau vide. Cela va vider la base puis relancer le calcul. Confirmer ?')) return;
      try {
        const r = await fetch(`${API}/prevision`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
        if (!r.ok) throw new Error(await r.text());
        lancerScript();
      } catch (e) { alert('Erreur suppression : ' + e.message); }
      return;
    }
    if (!window.confirm('Cela va remplacer toutes les données en base puis relancer le calcul. Confirmer ?')) return;
    const toutesLesEntrees = previsions.map(p => ({
      Item: p.ITEM, Mois: p.mois,
      Prevision: Number(p.Prevision) || 0,
      Commande_ferme: Number(p['Commande ferme']) || 0,
    }));
    try {
      const r1 = await fetch(`${API}/prevision`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      if (!r1.ok) throw new Error(await r1.text());
      const r2 = await fetch(`${API}/prevision`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toutesLesEntrees),
      });
      if (!r2.ok) throw new Error(await r2.text());
      lancerScript();
    } catch (e) { alert('Erreur sauvegarde : ' + e.message); }
  };

  const handleSupprimer = (itemASupprimer) => {
    if (!window.confirm(`Supprimer "${itemASupprimer}" du tableau ?`)) return;
    setPrevisions(prev => prev.filter(p => p.ITEM !== itemASupprimer));
  };


  const handleInputChange = (event, item, mois) => {
    const { value } = event.target;
    setPrevisions(prev => prev.map(p => (p.ITEM === item && p.mois === mois ? { ...p, Prevision: value } : p)));
  };

  /* ===================================================
     CALCULS
     =================================================== */
  const mois = useMemo(() => {
    const m = [...new Set(previsions.map(i => i.mois))];
    return m.sort((a, b) => {
      const ia = ORDRE_MOIS.indexOf(a), ib = ORDRE_MOIS.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [previsions]);

  const moisFuturs = mois.filter(m => (OFFSET_PAR_LABEL[m] ?? 0) >= 0);
  const mois3 = moisFuturs.slice(0, 3);

  // Index ITEM → mois → données
  const index = useMemo(() => {
    const idx = {};
    previsions.forEach(p => {
      if (!idx[p.ITEM]) idx[p.ITEM] = {};
      idx[p.ITEM][p.mois] = { prev: Number(p.Prevision) || 0, ferme: Number(p['Commande ferme']) || 0 };
    });
    return idx;
  }, [previsions]);

  const items = useMemo(() => [...new Set(previsions.map(i => i.ITEM))], [previsions]);

  // Statut de chaque référence = pire niveau sur les 3 prochains mois
  const lignes = items.map(item => {
    let pire = 'ok';
    mois3.forEach(m => {
      const d = index[item]?.[m] || { prev: 0, ferme: 0 };
      const n = niveau(d.prev, d.ferme);
      if (RANG[n] > RANG[pire]) pire = n;
    });
    let totPrev = 0, totFerme = 0;
    moisFuturs.forEach(m => {
      const d = index[item]?.[m] || { prev: 0, ferme: 0 };
      totPrev += d.prev; totFerme += d.ferme;
    });
    return { item, statut: pire, totPrev, totFerme, totRest: Math.max(0, totPrev - totFerme) };
  });

  const testFiltre = {
    all: () => true,
    over: l => l.statut === 'over',
    low: l => l.statut === 'low',
    ok: l => l.statut === 'ok',
  };

  const lignesVisibles = lignes.filter(testFiltre[filtre]);

  // Couverture moyenne par mois
  const moyenneMois = (m) => {
    let somme = 0, n = 0;
    items.forEach(item => {
      const d = index[item]?.[m];
      if (d && d.prev > 0) { somme += Math.min((d.ferme / d.prev) * 100, 200); n++; }
    });
    return n ? Math.round(somme / n) : 0;
  };

  const niveauMoyen = (pct) => (pct > 100 ? 'over' : pct >= SEUIL_OK ? 'ok' : pct >= SEUIL_FAIBLE ? 'mid' : 'low');

  const suggestionsFiltrees = references
    .filter(r => (`${r.item} ${r.desc}`).toLowerCase().includes(searchRef.toLowerCase()))
    .slice(0, 30);

  const premierMois = moisFuturs[0];
  const dernierMois = moisFuturs[moisFuturs.length - 1];

  /* ===================================================
     RENDU — CELLULES MOIS (3 lignes par référence)
     =================================================== */
  const donnees = (item, m) => {
    const d = index[item]?.[m];
    const prev = d ? d.prev : 0;
    const ferme = d ? d.ferme : 0;
    const pct = prev > 0 ? Math.round((ferme / prev) * 100) : null;
    return { prev, ferme, pct, n: niveau(prev, ferme), bloque: MOIS_BLOQUES.includes(m) };
  };

  // Ligne 1 — Prévision (saisissable)
  const renderPrevision = (item, m) => {
    const { bloque } = donnees(item, m);
    const brut = previsions.find(p => p.ITEM === item && p.mois === m);
    return (
      <td key={m} className={`sp-cell sp-cell-prev ${bloque ? 'sp-cell-locked' : ''} ${m === 'M' ? 'sp-col-current' : ''}`}>
        <input
          type="number"
          min="0"
          className="sp-cell-input"
          value={brut ? brut.Prevision : ''}
          onChange={(e) => handleInputChange(e, item, m)}
          disabled={bloque}
          aria-label={`Prévision ${item} ${getMoisLabel(m)}`}
        />
      </td>
    );
  };

  // Ligne 2 — Commande ferme
  const renderCommande = (item, m) => {
    const { ferme, bloque } = donnees(item, m);
    return (
      <td key={m} className={`sp-cell sp-cell-ferme ${bloque ? 'sp-cell-locked' : ''} ${m === 'M' ? 'sp-col-current' : ''}`}>
        {ferme ? fr(ferme) : <span className="sp-zero">0</span>}
      </td>
    );
  };

  // Ligne 3 — % de couverture (couleur selon le niveau)
  const renderPourcentage = (item, m) => {
    const { ferme, pct, n, bloque } = donnees(item, m);
    const largeur = pct === null ? (ferme > 0 ? 100 : 0) : Math.min(pct, 100);
    return (
      <td key={m} className={`sp-cell sp-cell-pct sp-lvl-${n} ${bloque ? 'sp-cell-locked' : ''}`}>
        <span className="sp-pct-value">{pct === null ? (ferme > 0 ? '∞' : '—') : `${pct}%`}</span>
        <div className="sp-gauge"><div className="sp-gauge-fill" style={{ width: `${largeur}%` }} /></div>
      </td>
    );
  };

  /* ===================================================
     RENDU
     =================================================== */
  return (
    <div className="sp-page">

      {/* ── Modal recalcul ── */}
      {isRunning && (
        <div className="sp-overlay">
          <div className="sp-modal" role="dialog" aria-modal="true" aria-label="Mise à jour des données">
            <div className={`sp-modal-head ${scriptError ? 'error' : ''}`}>
              <div className="sp-modal-kicker">Mise à jour des données</div>
              <div className="sp-modal-title-row">
                <span className="sp-modal-title">
                  {scriptError ? errorMsg : isDone ? 'Terminé avec succès' : `${ETAPES[Math.min(stepIndex, ETAPES.length - 1)]}…`}
                </span>
                <span className="sp-modal-pct">{progress}%</span>
              </div>
              <div className="sp-modal-track"><div className="sp-modal-fill" style={{ width: `${progress}%` }} /></div>
            </div>
            <div className="sp-steps">
              {ETAPES.map((label, i) => {
                const done = isDone || i < stepIndex;
                const current = !isDone && !scriptError && i === stepIndex;
                const failed = scriptError && i === stepIndex;
                return (
                  <div key={label} className={`sp-step ${done ? 'done' : ''} ${current ? 'current' : ''} ${failed ? 'failed' : ''}`}>
                    <span className="sp-step-icon">
                      {done && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                      )}
                      {failed && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                      )}
                    </span>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
            <div className="sp-modal-foot">
              {(isDone || scriptError) ? (
                <button type="button" className={`sp-btn ${scriptError ? 'sp-btn-danger' : 'sp-btn-dark'}`} onClick={fermerModal}>
                  {scriptError ? 'Fermer (erreur)' : 'Fermer'}
                </button>
              ) : (
                <span className="sp-muted">Calcul en cours, ne ferme pas la page…</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal fiche article ── */}
      {modalItem && (
        <div className="sp-overlay sp-overlay-top" onClick={() => setModalItem(null)}>
          <div className="sp-fiche-modal" onClick={e => e.stopPropagation()}>
            <button type="button" className="sp-btn sp-btn-ghost sp-fiche-close" onClick={() => setModalItem(null)}>
              Fermer
            </button>
            <Fichearticle itemPrefill={modalItem} />
          </div>
        </div>
      )}

      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Suiviprevision.css).
            Couvre aussi les champs de saisie des prévisions, le bloc référence et le pied « Couverture moyenne ». */}
      <style>{`
        .sp-table th, .sp-table td { text-align: center !important; vertical-align: middle; }
        .sp-table th .sp-th-key, .sp-table th .sp-th-label { text-align: center; align-self: center; }
        .sp-table .sp-cell-input { text-align: center; }
        .sp-table .sp-ref-body { text-align: center; align-items: center; }
        .sp-table .sp-ref-top { justify-content: center; }
        .sp-table .sp-gauge { margin-left: auto; margin-right: auto; }
      `}</style>
      <div className="sp-container">

        {/* ── Header ── */}
        <div className="sp-header">
          <div className="sp-header-left">
            <div className="sp-title-row">
              <div className="sp-accent" />
              <h1 className="sp-title">Suivi des prévisions</h1>
            </div>
            <div className="sp-subtitle">
              {premierMois && dernierMois
                ? `${moisFuturs.length} mois glissants · ${getMoisLabel(premierMois)} → ${getMoisLabel(dernierMois)} · prévisions comparées aux commandes fermes`
                : 'Prévisions comparées aux commandes fermes'}
            </div>
          </div>

          <div className="sp-header-actions">
            {/* Recherche / ajout de référence */}
            <div className="sp-search">
              <svg className="sp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                type="text"
                className="sp-search-input"
                value={searchRef}
                placeholder="Ajouter une référence…"
                aria-label="Rechercher une référence à ajouter"
                onChange={(e) => { setSearchRef(e.target.value); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              {showSuggestions && searchRef && (
                <div className="sp-suggestions">
                  {suggestionsFiltrees.length > 0 ? suggestionsFiltrees.map(r => (
                    <button
                      type="button"
                      key={r.item}
                      className="sp-suggestion"
                      onMouseDown={() => { setSearchRef(''); setShowSuggestions(false); handleItemChange(r.item); }}
                    >
                      <span className="sp-suggestion-ref">{r.item}</span>
                      {r.desc && <span className="sp-suggestion-desc">{r.desc}</span>}
                    </button>
                  )) : (
                    <div className="sp-suggestion-empty">Aucun résultat</div>
                  )}
                </div>
              )}
            </div>

            <button type="button" className="sp-btn sp-btn-primary" onClick={handleSave} disabled={isRunning}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
              {isRunning ? 'En cours…' : 'Enregistrer & recalculer'}
            </button>
          </div>
        </div>

        {/* ── Filtres ── */}
        <div className="sp-toolbar">
          <div className="sp-chips" role="group" aria-label="Filtrer les références">
            {FILTRES.map(f => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filtre === f.key}
                className={`sp-chip ${filtre === f.key ? 'active' : ''}`}
                onClick={() => setFiltre(f.key)}
              >
                {f.label}
                <span className="sp-chip-count">{lignes.filter(testFiltre[f.key]).length}</span>
              </button>
            ))}
          </div>
          <div className="sp-hint">Saisir la prévision directement dans les cellules · cliquer sur une référence pour ouvrir sa fiche article</div>
        </div>

        {/* ── Tableau ── */}
        <div className="sp-card sp-table-card">
          <div className="sp-table-scroll">
            <table className="sp-table">
              <thead>
                <tr>
                  <th className="sp-th-ref">Référence</th>
                  <th className="sp-th-type" aria-label="Type de ligne" />
                  <th className="sp-th-total">
                    <span className="sp-th-key">Total</span>
                    <span className="sp-th-label">{moisFuturs.length} mois</span>
                  </th>
                  {mois.map(m => (
                    <th key={m} className={m === 'M' ? 'sp-th-current' : ''}>
                      <span className="sp-th-key">{m}</span>
                      <span className="sp-th-label">{getMoisLabel(m)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignesVisibles.length === 0 && (
                  <tr>
                    <td colSpan={3 + mois.length} className="sp-empty">
                      {items.length === 0
                        ? 'Aucune prévision — recherchez une référence pour commencer'
                        : 'Aucune référence pour ce filtre'}
                    </td>
                  </tr>
                )}

                {lignesVisibles.map(ligne => (
                  <React.Fragment key={ligne.item}>
                    <tr className="sp-row sp-row-prev">
                      <td className="sp-td-ref" rowSpan={3}>
                        <div className="sp-ref">
                          <div className="sp-ref-body">
                            <div className="sp-ref-top">
                              <button
                                type="button"
                                className="sp-ref-link"
                                title="Ouvrir la fiche article"
                                onClick={() => setModalItem(ligne.item)}
                              >
                                {ligne.item}
                              </button>
                              <span className={`sp-status sp-status-${ligne.statut}`}>{STATUT[ligne.statut]}</span>
                            </div>
                            {descMap[ligne.item] && <span className="sp-ref-desc">{descMap[ligne.item]}</span>}
                          </div>
                          <button
                            type="button"
                            className="sp-icon-btn sp-icon-btn-delete"
                            aria-label={`Retirer ${ligne.item} du suivi`}
                            onClick={() => handleSupprimer(ligne.item)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
                          </button>
                        </div>
                      </td>
                      <td className="sp-td-type">Prévision</td>
                      <td className="sp-td-total" title="Somme des prévisions">{fr(ligne.totPrev)}</td>
                      {mois.map(m => renderPrevision(ligne.item, m))}
                    </tr>
                    <tr className="sp-row sp-row-ferme">
                      <td className="sp-td-type">Commande ferme</td>
                      <td className="sp-td-total" title="Somme des commandes fermes">{fr(ligne.totFerme)}</td>
                      {mois.map(m => renderCommande(ligne.item, m))}
                    </tr>
                    <tr className="sp-row sp-row-pct">
                      <td className="sp-td-type">% couverture</td>
                      <td className="sp-td-total sp-td-total-rest" title="Prévision restante">
                        <span className="sp-total-rest-label">reste</span> {fr(ligne.totRest)}
                      </td>
                      {mois.map(m => renderPourcentage(ligne.item, m))}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="sp-td-ref sp-tfoot-label" colSpan={2}>Couverture moyenne</td>
                    <td className="sp-td-total sp-tfoot-total" />
                    {mois.map(m => {
                      const pct = moyenneMois(m);
                      return <td key={m} className={`sp-tfoot-val sp-lvl-text-${niveauMoyen(pct)}`}>{pct}%</td>;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Suiviprevision;